import { steps } from '../config/steps'
import { useReveal } from '../hooks/useReveal'
import { Icon } from './ui/Icon'

/**
 * Hand-drawn-feeling connector between two steps. `data-reveal="draw"` grows
 * it from its left edge, so it reads as the line being drawn toward the next
 * step once the previous one has landed. `seq` is the slot in the
 * step → connector → step queue (see .seq-* in index.css).
 */
function ConnectorPath({ flip = false, seq }) {
  return (
    <div
      className={`hidden shrink-0 items-center pb-16 lg:flex seq-${seq}`}
      data-reveal="draw"
      aria-hidden="true"
    >
      <svg viewBox="0 0 160 60" className="h-14 w-40 text-brand-400" fill="none">
        <path
          d={flip ? 'M4 14C40 62 108 62 146 20' : 'M4 44C42 -2 110 -2 146 38'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="3 9"
          opacity="0.75"
        />
        <path
          d={flip ? 'M138 12l9 8-11 5' : 'M138 30l9 8-11 5'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

export function HowItWorks() {
  // Reveals once and stays. `once: false` used to replay the step → connector
  // → step queue on every visit, but each item only finishes its staggered
  // fade-in after its `--reveal-delay` (up to 1680ms) elapses — scrolling
  // past the section and back before that, or even just scrolling through it
  // at normal speed, re-hid items the observer had already un-intersected,
  // which read as the content going blank/white mid-scroll.
  const ref = useReveal()

  return (
    <section
      id="how-it-works"
      ref={ref}
      className="scroll-mt-20 bg-brand-50 pt-24 pb-12 sm:pt-32 sm:pb-16 dark:bg-[var(--surface-soft)]"
    >
      <div className="mx-auto max-w-[88rem] px-5 sm:px-8">
        <header className="mx-auto max-w-3xl text-center">
          <p className="text-base font-bold text-brand-600 dark:text-brand-300" data-reveal>
            How RoamMyWay Works
          </p>
          <h2
            className="mt-4 text-[clamp(2rem,4.4vw,3.4rem)] leading-[1.08] font-bold"
            data-reveal
            style={{ '--reveal-delay': '80ms' }}
          >
            From Idea To Itinerary In Three Steps
          </h2>
        </header>

        <ol className="mt-16 flex flex-col items-stretch gap-12 lg:mt-20 lg:flex-row lg:items-start lg:justify-center lg:gap-0">
          {/* Queue: step 1 (seq 0) → connector (seq 1) → step 2 (seq 2) →
              connector (seq 3) → step 3 (seq 4). Delays live in .seq-*. */}
          {steps.map((step, i) => (
            <li key={step.number} className="contents">
              {i > 0 && <ConnectorPath flip={i === 2} seq={i * 2 - 1} />}
              <div
                className={`flex flex-1 flex-col items-center px-4 text-center lg:max-w-xs ${i > 0 ? `seq-${i * 2}` : ''}`}
                data-reveal="rise"
              >
                <div className={`relative ${i > 0 ? `seq-${i * 2}` : ''}`} data-reveal="pop">
                  <span className="grid h-20 w-20 place-items-center rounded-2xl bg-white text-brand-600 shadow-[0_10px_30px_-12px_rgb(45_101_113/0.45)] dark:bg-[var(--surface-tint)] dark:text-brand-300">
                    <Icon name={step.icon} className="h-8 w-8" strokeWidth={1.6} />
                  </span>
                  <span className="absolute -top-2 -right-2 rounded-full bg-brand-600 px-2 py-0.5 font-mono text-[0.65rem] font-bold text-white">
                    {step.number}
                  </span>
                </div>
                <h3 className="mt-7 text-2xl font-semibold text-accent-500">{step.title}</h3>
                <p className="mt-3 max-w-[28ch] text-[1.02rem] leading-relaxed">{step.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}
